import React, { useCallback, useEffect, useState, useRef } from 'react';
import { usePages } from './hooks/usePages';
import { Sidebar } from './components/Sidebar';
import { PageTitle } from './components/PageTitle';
import { BlockEditor } from './components/BlockEditor';
import { DeleteConfirmDialog } from './components/DeleteConfirmDialog';
import { FeatureTemplate } from './components/FeatureTemplate';
import { PersonaTemplate } from './components/PersonaTemplate';
import { TestTemplate } from './components/TestTemplate';
import { OnboardingTour, OnboardingStep, STEP_ORDER } from './components/OnboardingTour';
import { ChevronRightIcon, MoreHorizontalIcon, Trash2Icon, PlusIcon, FlaskConicalIcon, BoxIcon, UsersIcon, ArrowUpRightIcon, XIcon, SearchIcon, FileTextIcon } from 'lucide-react';
const SECTION_TITLES: Record<string, string> = {
  home: 'Home',
  'product-hub': 'Feature Hub',
  'persona-hub': 'Persona Hub',
  tests: 'Test Hub'
};
const SECTION_DESCRIPTIONS: Record<string, string> = {
  home: 'Your product workspace overview.',
  'product-hub': 'Your product information and strategy live here.',
  'persona-hub': 'Define and manage your user personas here.',
  tests: 'Create and manage your tests here.'
};
const SECTION_NOUNS: Record<string, string> = {
  'product-hub': 'Feature',
  'persona-hub': 'Persona',
  tests: 'Test'
};
const SECTION_DEFAULT_TITLES: Record<string, string> = {
  'product-hub': 'New Feature',
  'persona-hub': 'New Persona',
  tests: 'New Test'
};
const HUB_SECTIONS = new Set(['product-hub', 'persona-hub', 'tests']);
// Tab state
type Tab = {
  id: string;
  sectionId: string;
  pageId: string | null;
  title: string;
};
export function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [pendingDeletePageId, setPendingDeletePageId] = useState<string | null>(null);
  const [newlyAddedSectionId, setNewlyAddedSectionId] = useState<string | null>(null);
  const [associations, setAssociations] = useState<Record<string, string[]>>({});
  const [featureFields, setFeatureFields] = useState<Record<string, Record<string, string>>>({});
  const [testData, setTestData] = useState<Record<string, {
    prototypeLink: string;
    featureId: string;
    personaIds: string[];
    testType: string;
    componentFocuses: string[];
    hypothesis: string;
    isRun: boolean;
  }>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const createDropdownRef = useRef<HTMLDivElement>(null);
  // Tab state
  const [tabs, setTabs] = useState<Tab[]>([{
    id: 'tab-home',
    sectionId: 'home',
    pageId: null,
    title: 'Home'
  }]);
  const [activeTabId, setActiveTabId] = useState('tab-home');
  const [showTabModal, setShowTabModal] = useState(false);
  const [tabModalSearch, setTabModalSearch] = useState('');
  const {
    pages,
    activePage,
    activePageId,
    addPage,
    deletePage,
    setActivePage,
    clearActivePage,
    updatePageTitle,
    updatePageTitleById,
    addBlock,
    updateBlock,
    deleteBlock,
    changeBlockType
  } = usePages();
  const mainRef = useRef<HTMLElement>(null);
  // Get active tab
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];
  // Sync activeSection and activePageId with active tab
  useEffect(() => {
    if (activeTab) {
      setActiveSection(activeTab.sectionId);
      if (activeTab.pageId) {
        setActivePage(activeTab.pageId);
      } else {
        clearActivePage();
      }
    }
  }, [activeTab, setActivePage, clearActivePage]);
  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside() {
      if (openMenuId) setOpenMenuId(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);
  // Close create dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (createDropdownRef.current && !createDropdownRef.current.contains(e.target as Node)) {
        setShowCreateDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Demo shortcut: Cmd+Shift+R restarts the onboarding tour
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.metaKey && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        try {
          localStorage.removeItem('onboarding-complete');
        } catch {}
        setOnboardingStep('feature-hub-nav');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  // Onboarding state
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(() => {
    try {
      const done = localStorage.getItem('onboarding-complete');
      return done ? null : 'feature-hub-nav';
    } catch {
      return 'feature-hub-nav';
    }
  });
  // Ensure Hubs are expanded during onboarding steps that need them
  // (handled by Sidebar's auto-expand on activeSection change)
  const handleAssociationChange = useCallback((sourceId: string, newTargetIds: string[]) => {
    setAssociations(prev => {
      const next = {
        ...prev
      };
      const oldTargetIds = prev[sourceId] || [];
      const removed = oldTargetIds.filter(id => !newTargetIds.includes(id));
      const added = newTargetIds.filter(id => !oldTargetIds.includes(id));
      next[sourceId] = newTargetIds;
      for (const targetId of removed) {
        next[targetId] = (next[targetId] || []).filter(id => id !== sourceId);
      }
      for (const targetId of added) {
        const current = next[targetId] || [];
        if (!current.includes(sourceId)) {
          next[targetId] = [...current, sourceId];
        }
      }
      return next;
    });
  }, []);
  const handleSelectSection = useCallback((sectionId: string, openInNewTab = false) => {
    if (openInNewTab) {
      const existingTab = tabs.find(tab => tab.sectionId === sectionId && tab.pageId === null);
      if (existingTab) {
        return;
      } else {
        const newTab: Tab = {
          id: `tab-${Date.now()}`,
          sectionId,
          pageId: null,
          title: SECTION_TITLES[sectionId] || 'Untitled'
        };
        setTabs(prev => [...prev, newTab]);
      }
    } else {
      setTabs(prev => prev.map(tab => tab.id === activeTabId ? {
        ...tab,
        sectionId,
        pageId: null,
        title: SECTION_TITLES[sectionId] || 'Untitled'
      } : tab));
      setActiveSection(sectionId);
      clearActivePage();
    }
  }, [clearActivePage, activeTabId, tabs]);
  // Onboarding handlers — defined AFTER handleSelectSection to avoid TDZ error
  const handleOnboardingNext = useCallback(() => {
    if (!onboardingStep) return;
    const idx = STEP_ORDER.indexOf(onboardingStep);
    if (idx === -1) return;
    // Welcome step: finish onboarding and navigate to Feature Hub with a new feature
    if (onboardingStep === 'welcome') {
      setOnboardingStep(null);
      try {
        localStorage.setItem('onboarding-complete', '1');
      } catch {}
      handleSelectSection('product-hub');
      const page = addPage('product-hub');
      clearActivePage();
      setNewlyAddedSectionId(page.id);
      return;
    }
    if (idx === STEP_ORDER.length - 1) {
      setOnboardingStep(null);
      try {
        localStorage.setItem('onboarding-complete', '1');
      } catch {}
      return;
    }
    const next = STEP_ORDER[idx + 1];
    if (onboardingStep === 'feature-hub-nav') {
      handleSelectSection('product-hub');
    } else if (onboardingStep === 'new-feature-button') {
      setActiveSection('product-hub');
      const page = addPage('product-hub');
      clearActivePage();
      setNewlyAddedSectionId(page.id);
    } else if (onboardingStep === 'feature-template') {
      handleSelectSection('persona-hub');
    } else if (onboardingStep === 'new-persona-button') {
      setActiveSection('persona-hub');
      const page = addPage('persona-hub');
      clearActivePage();
      setNewlyAddedSectionId(page.id);
    } else if (onboardingStep === 'persona-template') {
      handleSelectSection('tests');
    } else if (onboardingStep === 'test-hub-nav') {
      handleSelectSection('tests');
    } else if (onboardingStep === 'new-test-button') {
      setActiveSection('tests');
      const page = addPage('tests');
      const testCount = pages.filter(p => p.parentSection === 'tests').length + 1;
      const padded = String(testCount).padStart(2, '0');
      updatePageTitleById(page.id, `UAT ${padded}: [Test Name]`);
      clearActivePage();
      setNewlyAddedSectionId(page.id);
    }
    // test-template → welcome: no navigation needed
    setOnboardingStep(next);
  }, [onboardingStep, handleSelectSection, addPage, clearActivePage, pages, updatePageTitleById]);
  const handleOnboardingBack = useCallback(() => {
    if (!onboardingStep) return;
    const idx = STEP_ORDER.indexOf(onboardingStep);
    if (idx <= 0) return;
    const prev = STEP_ORDER[idx - 1];
    if (prev === 'new-feature-button' || prev === 'feature-template') {
      handleSelectSection('product-hub');
    } else if (prev === 'persona-hub-nav' || prev === 'new-persona-button' || prev === 'persona-template') {
      handleSelectSection('persona-hub');
    } else if (prev === 'test-hub-nav' || prev === 'new-test-button' || prev === 'test-template') {
      handleSelectSection('tests');
    }
    setOnboardingStep(prev);
  }, [onboardingStep, handleSelectSection]);
  const handleOnboardingSkip = useCallback(() => {
    setOnboardingStep(null);
    try {
      localStorage.setItem('onboarding-complete', '1');
    } catch {}
  }, []);
  const handleAddChildPage = useCallback((sectionId: string) => {
    setActiveSection(sectionId);
    const page = addPage(sectionId);
    if (sectionId === 'tests') {
      const testCount = pages.filter(p => p.parentSection === 'tests').length + 1;
      const padded = String(testCount).padStart(2, '0');
      updatePageTitleById(page.id, `UAT ${padded}: [Test Name]`);
    }
    if (HUB_SECTIONS.has(sectionId)) {
      clearActivePage();
      setNewlyAddedSectionId(page.id);
    }
  }, [addPage, clearActivePage, pages, updatePageTitleById]);
  const handleSelectPage = useCallback((pageId: string, openInNewTab = false) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      if (openInNewTab) {
        // Check if a tab with this page already exists
        const existingTab = tabs.find(tab => tab.pageId === pageId);
        if (existingTab) {
          // Switch to existing tab instead of creating duplicate
          setActiveTabId(existingTab.id);
        } else {
          // Create new tab without switching to it
          const newTab: Tab = {
            id: `tab-${Date.now()}`,
            sectionId: page.parentSection,
            pageId: page.id,
            title: page.title
          };
          setTabs(prev => [...prev, newTab]);
        }
        // Don't change activeSection or activePage - stay on current view
      } else {
        // Update current tab
        setTabs(prev => prev.map(tab => tab.id === activeTabId ? {
          ...tab,
          sectionId: page.parentSection,
          pageId: page.id,
          title: page.title
        } : tab));
        setActiveSection(page.parentSection);
        // For hub sections, stay on the hub main page and scroll to section
        if (HUB_SECTIONS.has(page.parentSection)) {
          clearActivePage();
          // Scroll to the section after render
          requestAnimationFrame(() => {
            const sectionElement = document.getElementById(`section-${pageId}`);
            if (sectionElement) {
              sectionElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          });
        } else {
          setActivePage(pageId);
        }
      }
    }
  }, [pages, setActivePage, clearActivePage, activeTabId, tabs]);
  // Tab management functions
  const handleCloseTab = useCallback((tabId: string) => {
    // Prevent closing the last remaining tab
    if (tabs.length <= 1) {
      return;
    }
    setTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);
      // Keep at least one tab
      if (filtered.length === 0) {
        return [{
          id: 'tab-home',
          sectionId: 'home',
          pageId: null,
          title: 'Home'
        }];
      }
      return filtered;
    });
    // If closing active tab, switch to another tab
    if (tabId === activeTabId) {
      setTabs(prev => {
        const filtered = prev.filter(t => t.id !== tabId);
        if (filtered.length > 0) {
          const currentIndex = prev.findIndex(t => t.id === tabId);
          const nextTab = filtered[Math.max(0, currentIndex - 1)];
          setActiveTabId(nextTab.id);
        }
        return filtered;
      });
    }
  }, [activeTabId, tabs.length]);
  const handleAddTab = useCallback(() => {
    setShowTabModal(true);
  }, []);
  const handleSwitchTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);
  const handleOpenInNewTab = useCallback((sectionId: string, pageId: string | null, title: string) => {
    // Check if a tab with this exact page/section already exists
    const existingTab = tabs.find(tab => tab.sectionId === sectionId && tab.pageId === pageId);
    if (existingTab) {
      // Switch to existing tab instead of creating duplicate
      setActiveTabId(existingTab.id);
    } else {
      // Create new tab and switch to it
      const newTab: Tab = {
        id: `tab-${Date.now()}`,
        sectionId,
        pageId,
        title
      };
      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);
    }
    setShowTabModal(false);
    setTabModalSearch('');
  }, [tabs]);
  const handleRequestDeletePage = useCallback((pageId: string) => {
    setPendingDeletePageId(pageId);
  }, []);
  const handleCancelDelete = useCallback(() => {
    setPendingDeletePageId(null);
  }, []);
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeletePageId) {
      // Clean up associations when deleting a page
      setAssociations(prev => {
        const next = {
          ...prev
        };
        const targetIds = next[pendingDeletePageId] || [];
        for (const targetId of targetIds) {
          next[targetId] = (next[targetId] || []).filter(id => id !== pendingDeletePageId);
        }
        delete next[pendingDeletePageId];
        return next;
      });
      deletePage(pendingDeletePageId);
      setPendingDeletePageId(null);
    }
  }, [pendingDeletePageId, deletePage]);
  const handleArchive = useCallback(() => {
    setPendingDeletePageId(null);
  }, []);
  const pendingDeletePage = pendingDeletePageId ? pages.find(p => p.id === pendingDeletePageId) ?? null : null;
  const pendingDeleteNoun = pendingDeletePage ? SECTION_NOUNS[pendingDeletePage.parentSection] ?? 'Page' : 'Page';
  const childPages = pages.map(p => ({
    id: p.id,
    label: p.title,
    parentSection: p.parentSection
  }));
  const isProductPage = activePage && activePage.parentSection === 'product-hub';
  const isPersonaPage = activePage && activePage.parentSection === 'persona-hub';
  const isTitleDefault = activePage && activePage.title === SECTION_DEFAULT_TITLES[activePage.parentSection];
  // Get hub sections for the active section
  const hubSections = HUB_SECTIONS.has(activeSection) ? pages.filter(p => p.parentSection === activeSection) : [];
  // Get persona options with IDs for the multi-select in FeatureTemplate
  const personaOptionsWithIds = pages.filter(p => p.parentSection === 'persona-hub').filter(p => p.title.trim() && p.title !== SECTION_DEFAULT_TITLES['persona-hub']).map(p => ({
    id: p.id,
    name: p.title
  }));
  // Get feature options with IDs for the multi-select in PersonaTemplate
  const featureOptionsWithIds = pages.filter(p => p.parentSection === 'product-hub').filter(p => p.title.trim() && p.title !== SECTION_DEFAULT_TITLES['product-hub']).map(p => ({
    id: p.id,
    name: p.title
  }));
  // Get all tests with run status
  const allTests = pages.filter(p => p.parentSection === 'tests').map(p => ({
    ...p,
    isRun: testData[p.id]?.isRun || false
  }));
  // Get recent tests (run tests, sorted by most recent)
  const recentTests = allTests.filter(t => t.isRun).slice(0, 5);
  // Organize pages by time period for tab modal
  const now = Date.now();
  const eightHoursAgo = now - 8 * 60 * 60 * 1000;
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const fiveDaysAgo = now - 5 * 24 * 60 * 60 * 1000;
  const todayPages = pages.filter(p => p.createdAt >= eightHoursAgo);
  const yesterdayPages = pages.filter(p => p.createdAt < eightHoursAgo && p.createdAt >= oneDayAgo);
  const pastWeekPages = pages.filter(p => p.createdAt < oneDayAgo && p.createdAt >= fiveDaysAgo);
  // Filter pages based on search
  const filterPages = (pageList: typeof pages) => {
    if (!tabModalSearch.trim()) return pageList;
    const search = tabModalSearch.toLowerCase();
    return pageList.filter(p => p.title.toLowerCase().includes(search));
  };
  return <div className="flex h-full w-full bg-white" data-id="element-0">
      <Sidebar activeSection={activeSection} activePageId={activePageId} childPages={childPages} onSelectSection={handleSelectSection} onSelectPage={handleSelectPage} onAddChildPage={handleAddChildPage} onDeletePage={handleRequestDeletePage} onboardingStep={onboardingStep} onRestartOnboarding={() => {
      try {
        localStorage.removeItem('onboarding-complete');
      } catch {}
      setOnboardingStep('feature-hub-nav');
    }} data-id="element-1" />

      <main ref={mainRef} className="flex-1 overflow-y-auto flex flex-col" data-id="element-2">
        {/* Tab Bar */}
        <div className="flex-shrink-0 border-b border-stone-200 bg-stone-50/50" data-id="element-3">
          <div className="flex items-center gap-0.5 px-2 overflow-x-auto" data-id="element-4">
            {tabs.map(tab => <button key={tab.id} onClick={() => handleSwitchTab(tab.id)} className={`group flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition-colors duration-100 border-b-2 ${tab.id === activeTabId ? 'border-stone-900 text-stone-900 bg-white' : 'border-transparent text-stone-600 hover:text-stone-900 hover:bg-stone-100/50'}`} data-id="element-5">
                <span className="truncate max-w-[180px]" data-id="element-6">{tab.title}</span>
                {tabs.length > 1 && <button onClick={e => {
              e.stopPropagation();
              handleCloseTab(tab.id);
            }} className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-none hover:bg-stone-200 opacity-0 group-hover:opacity-100 transition-opacity duration-100" aria-label="Close tab" data-id="element-7">
                    <XIcon className="w-3 h-3" data-id="element-8" />
                  </button>}
              </button>)}
            <button onClick={handleAddTab} className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-none transition-colors duration-100" aria-label="New tab" data-id="element-9">
              <PlusIcon className="w-4 h-4" data-id="element-10" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-11">
          <div className="max-w-[840px] mx-auto px-6 py-16" data-id="element-12">
            {activePage ? <>
                <BlockEditor key={activePage.id} blocks={activePage.blocks} onAddBlock={addBlock} onUpdateBlock={updateBlock} onDeleteBlock={deleteBlock} onChangeBlockType={changeBlockType} data-id="element-13" />
              </> : activeSection === 'home' ? <>
                {/* Home Header */}
                <div className="flex items-center justify-between mb-8" data-id="element-14">
                  <h1 className="text-4xl font-bold text-stone-900 leading-tight" data-id="element-15">
                    Home
                  </h1>
                  <div ref={createDropdownRef} className="relative" data-id="element-16">
                    <button type="button" onClick={() => setShowCreateDropdown(!showCreateDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#0000ff] rounded-none hover:bg-[#3333ff] transition-colors duration-100" data-id="element-17">
                      <span data-id="element-18">Create New</span>
                      <PlusIcon className="w-4 h-4" data-id="element-19" />
                    </button>
                    {showCreateDropdown && <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-white border border-stone-200 rounded-none shadow-lg py-1" data-id="element-20">
                        <button type="button" onClick={() => {
                    handleAddChildPage('tests');
                    setShowCreateDropdown(false);
                  }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-21">
                          <FlaskConicalIcon className="w-4 h-4 text-stone-400" data-id="element-22" />
                          <span data-id="element-23">Test</span>
                        </button>
                        <button type="button" onClick={() => {
                    handleAddChildPage('product-hub');
                    setShowCreateDropdown(false);
                  }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-24">
                          <BoxIcon className="w-4 h-4 text-stone-400" data-id="element-25" />
                          <span data-id="element-26">Feature</span>
                        </button>
                        <button type="button" onClick={() => {
                    handleAddChildPage('persona-hub');
                    setShowCreateDropdown(false);
                  }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-27">
                          <UsersIcon className="w-4 h-4 text-stone-400" data-id="element-28" />
                          <span data-id="element-29">Persona</span>
                        </button>
                      </div>}
                  </div>
                </div>

                <p className="text-stone-400 text-base mt-[0px] mb-[32px]" data-id="element-30">
                  {SECTION_DESCRIPTIONS[activeSection] ?? ''}
                </p>

                {/* Recent Tests */}
                <div className="mb-12" data-id="element-31">
                  <h2 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-32">
                    Recent Tests
                  </h2>
                  {recentTests.length > 0 ? <div className="space-y-2" data-id="element-33">
                      {recentTests.map(test => <button key={test.id} type="button" onClick={e => handleSelectSection('tests', e.metaKey || e.ctrlKey)} className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 rounded-none hover:border-stone-300 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-34">
                          <FlaskConicalIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-35" />
                          <span className="flex-1 text-sm text-stone-900 truncate" data-id="element-36">
                            {test.title}
                          </span>
                          <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-none" data-id="element-37">
                            Run
                          </span>
                        </button>)}
                    </div> : <p className="text-sm text-stone-400 italic" data-id="element-38">
                      No tests have been run yet.
                    </p>}
                </div>

                {/* All Tests */}
                <div className="mb-12" data-id="element-39">
                  <div className="flex items-center gap-2 mb-4" data-id="element-40">
                    <h2 className="text-lg font-semibold text-stone-900" data-id="element-41">
                      All Tests
                    </h2>
                    <button type="button" onClick={e => handleSelectSection('tests', e.metaKey || e.ctrlKey)} className="group relative w-5 h-5 flex items-center justify-center rounded-none hover:bg-stone-100 transition-colors duration-100" aria-label="Open Test Hub" data-id="element-42">
                      <ArrowUpRightIcon className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-600 transition-colors duration-100" data-id="element-43" />
                      <span className="absolute left-full ml-2 px-2 py-1 bg-stone-900 text-white text-xs rounded-none whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none" data-id="element-44">
                        Open Test Hub
                      </span>
                    </button>
                  </div>
                  {allTests.length > 0 ? <div className="space-y-2" data-id="element-45">
                      {allTests.map(test => <button key={test.id} type="button" onClick={e => handleSelectSection('tests', e.metaKey || e.ctrlKey)} className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 rounded-none hover:border-stone-300 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-46">
                          <FlaskConicalIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-47" />
                          <span className="flex-1 text-sm text-stone-900 truncate" data-id="element-48">
                            {test.title}
                          </span>
                          {test.isRun && <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-none" data-id="element-49">
                              Run
                            </span>}
                        </button>)}
                    </div> : <p className="text-sm text-stone-400 italic" data-id="element-50">
                      No tests created yet.
                    </p>}
                </div>

                {/* Feature Hub */}
                <div className="mb-12" data-id="element-51">
                  <div className="flex items-center gap-2 mb-4" data-id="element-52">
                    <h2 className="text-lg font-semibold text-stone-900" data-id="element-53">
                      Feature Hub
                    </h2>
                    <button type="button" onClick={e => handleSelectSection('product-hub', e.metaKey || e.ctrlKey)} className="group relative w-5 h-5 flex items-center justify-center rounded-none hover:bg-stone-100 transition-colors duration-100" aria-label="Open Feature Hub" data-id="element-54">
                      <ArrowUpRightIcon className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-600 transition-colors duration-100" data-id="element-55" />
                      <span className="absolute left-full ml-2 px-2 py-1 bg-stone-900 text-white text-xs rounded-none whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none" data-id="element-56">
                        Open Feature Hub
                      </span>
                    </button>
                  </div>
                  {featureOptionsWithIds.length > 0 ? <div className="space-y-2" data-id="element-57">
                      {featureOptionsWithIds.map(feature => <button key={feature.id} type="button" onClick={e => handleSelectSection('product-hub', e.metaKey || e.ctrlKey)} className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 rounded-none hover:border-stone-300 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-58">
                          <BoxIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-59" />
                          <span className="flex-1 text-sm text-stone-900 truncate" data-id="element-60">
                            {feature.name}
                          </span>
                        </button>)}
                    </div> : <p className="text-sm text-stone-400 italic" data-id="element-61">
                      No features created yet.
                    </p>}
                </div>

                {/* Persona Hub */}
                <div className="mb-12" data-id="element-62">
                  <div className="flex items-center gap-2 mb-4" data-id="element-63">
                    <h2 className="text-lg font-semibold text-stone-900" data-id="element-64">
                      Persona Hub
                    </h2>
                    <button type="button" onClick={e => handleSelectSection('persona-hub', e.metaKey || e.ctrlKey)} className="group relative w-5 h-5 flex items-center justify-center rounded-none hover:bg-stone-100 transition-colors duration-100" aria-label="Open Persona Hub" data-id="element-65">
                      <ArrowUpRightIcon className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-600 transition-colors duration-100" data-id="element-66" />
                      <span className="absolute left-full ml-2 px-2 py-1 bg-stone-900 text-white text-xs rounded-none whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none" data-id="element-67">
                        Open Persona Hub
                      </span>
                    </button>
                  </div>
                  {personaOptionsWithIds.length > 0 ? <div className="space-y-2" data-id="element-68">
                      {personaOptionsWithIds.map(persona => <button key={persona.id} type="button" onClick={e => handleSelectSection('persona-hub', e.metaKey || e.ctrlKey)} className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 rounded-none hover:border-stone-300 hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-69">
                          <UsersIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-70" />
                          <span className="flex-1 text-sm text-stone-900 truncate" data-id="element-71">
                            {persona.name}
                          </span>
                        </button>)}
                    </div> : <p className="text-sm text-stone-400 italic" data-id="element-72">
                      No personas created yet.
                    </p>}
                </div>
              </> : <>
                {activeSection === 'persona-hub' && <div className="mb-3" data-id="element-73"></div>}
                <div className="flex items-center justify-between mb-2" data-id="element-74">
                  <h1 className="text-4xl font-bold text-stone-900 leading-tight" data-id="element-75">
                    {SECTION_TITLES[activeSection] ?? activeSection}
                  </h1>
                  <button id={activeSection === 'product-hub' ? 'onboarding-new-feature-btn' : activeSection === 'persona-hub' ? 'onboarding-new-persona-btn' : activeSection === 'tests' ? 'onboarding-new-test-btn' : undefined} type="button" onClick={() => handleAddChildPage(activeSection)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#0000ff] rounded-none hover:bg-[#3333ff] transition-colors duration-100" data-id="element-76">
                    <PlusIcon className="w-4 h-4" data-id="element-77" />
                    <span data-id="element-78">
                      {SECTION_DEFAULT_TITLES[activeSection] ?? 'New Item'}
                    </span>
                  </button>
                </div>

                <p className="text-stone-400 text-base mt-[0px] mb-[32px]" data-id="element-79">
                  {SECTION_DESCRIPTIONS[activeSection] ?? ''}
                </p>

                {/* Test Hub: split into UAT Tests and Other Tests */}
                {/* Non-test hubs: flat list */}
                {(() => {
              const isTestHub = activeSection === 'tests';
              const uatSections = isTestHub ? hubSections.filter(s => /^UAT\s/i.test(s.title)) : [];
              const otherSections = isTestHub ? hubSections.filter(s => !/^UAT\s/i.test(s.title)) : [];
              const renderSectionCard = (section: (typeof hubSections)[0], isLastInGroup: boolean) => {
                const isDefault = section.title === SECTION_DEFAULT_TITLES[section.parentSection] || section.parentSection === 'tests' && /^UAT \d+: \[Test Name\]$/.test(section.title);
                const isCollapsed = collapsedSections[section.id] ?? false;
                const isLastSection = hubSections.indexOf(section) === hubSections.length - 1;
                return <div key={section.id} id={isLastSection ? 'onboarding-hub-content' : `section-${section.id}`} className="mt-12 bg-gradient-to-b from-stone-50/80 to-stone-100/40 border border-stone-200/60 rounded-none px-6 py-6" data-id="element-80">
                        {section.parentSection === 'persona-hub' && <div className="mb-2" data-id="element-81">
                            <svg width="32" height="32" viewBox="0 0 124 123" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-82">
                              <path d="M28 24V29.7856C28 35.7975 29.755 41.6787 33.0498 46.7075C35.967 51.1601 39.9858 54.783 44.716 57.2244L45.5586 57.6593C50.4485 60.1831 55.8713 61.5 61.3741 61.5H62.9638C68.5815 61.5 74.1261 60.2267 79.181 57.7759L80.7331 57.0234C85.4963 54.7139 89.5797 51.2094 92.585 46.8517C96.1115 41.7383 98 35.6736 98 29.4621V24" stroke="black" strokeWidth="10" data-id="element-83" />
                              <circle cx="62.5" cy="40.5" r="8.5" fill="black" data-id="element-84" />
                              <path d="M24 99L48.8913 96.6066C49.6276 96.5358 50.3523 96.3742 51.0489 96.1254C54.6177 94.8508 57 91.4704 57 87.6808V74.32C57 71.1057 59.6057 68.5 62.82 68.5C64.2256 68.5 65.5838 69.0087 66.6436 69.9322L100 99" stroke="black" strokeWidth="10" data-id="element-85" />
                            </svg>
                          </div>}
                        <div className="flex items-center gap-1.5" data-id="element-86">
                          <div contentEditable suppressContentEditableWarning className={`flex-1 text-2xl font-semibold outline-none border-b border-transparent focus:border-stone-200 pb-1 transition-colors duration-100 ${isDefault ? 'text-stone-400' : 'text-stone-900'}`} onFocus={e => {
                      const el = e.target as HTMLDivElement;
                      const text = el.textContent ?? '';
                      const uatMatch = text.match(/^(UAT \d+:\s*)/);
                      if (uatMatch && el.firstChild) {
                        const prefixLen = uatMatch[1].length;
                        const range = document.createRange();
                        range.setStart(el.firstChild, prefixLen);
                        range.setEnd(el.firstChild, text.length);
                        const sel = window.getSelection();
                        sel?.removeAllRanges();
                        sel?.addRange(range);
                      } else if (isDefault) {
                        const range = document.createRange();
                        range.selectNodeContents(el);
                        const sel = window.getSelection();
                        sel?.removeAllRanges();
                        sel?.addRange(range);
                      }
                    }} onInput={e => {
                      const el = e.target as HTMLDivElement;
                      let newText = el.textContent ?? '';
                      // For test sections, preserve the UAT prefix
                      if (section.parentSection === 'tests') {
                        const prefixMatch = section.title.match(/^(UAT \d+:\s*)/);
                        if (prefixMatch) {
                          const prefix = prefixMatch[1];
                          if (!newText.startsWith(prefix)) {
                            // User deleted into the prefix — restore it
                            const afterPrefix = newText.replace(/^(UAT\s*\d*:?\s*)/, '');
                            newText = prefix + afterPrefix;
                            el.textContent = newText;
                            // Place cursor at end
                            requestAnimationFrame(() => {
                              if (el.firstChild) {
                                const range = document.createRange();
                                range.setStart(el.firstChild, newText.length);
                                range.collapse(true);
                                const sel = window.getSelection();
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                              }
                            });
                          }
                        }
                      }
                      updatePageTitleById(section.id, newText);
                      if (newlyAddedSectionId === section.id) {
                        setNewlyAddedSectionId(null);
                      }
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') e.preventDefault();
                      // Prevent backspace/delete from entering the UAT prefix
                      if (section.parentSection === 'tests' && (e.key === 'Backspace' || e.key === 'Delete')) {
                        const sel = window.getSelection();
                        const el = e.target as HTMLDivElement;
                        const text = el.textContent ?? '';
                        const prefixMatch = text.match(/^(UAT \d+:\s*)/);
                        if (prefixMatch && sel && sel.rangeCount > 0) {
                          const prefixLen = prefixMatch[1].length;
                          const range = sel.getRangeAt(0);
                          if (e.key === 'Backspace' && range.collapsed && range.startOffset <= prefixLen) {
                            e.preventDefault();
                          } else if (e.key === 'Delete' && range.collapsed && range.startOffset < prefixLen) {
                            e.preventDefault();
                          } else if (!range.collapsed && range.startOffset < prefixLen) {
                            // Selection extends into prefix — clamp it
                            e.preventDefault();
                            const newRange = document.createRange();
                            newRange.setStart(el.firstChild!, prefixLen);
                            newRange.setEnd(range.endContainer, range.endOffset);
                            sel.removeAllRanges();
                            sel.addRange(newRange);
                            document.execCommand('delete');
                          }
                        }
                      }
                    }} ref={el => {
                      if (el && el.textContent !== section.title) {
                        el.textContent = section.title;
                      }
                      if (el && newlyAddedSectionId === section.id) {
                        requestAnimationFrame(() => {
                          el.focus();
                          const text = el.textContent ?? '';
                          const uatMatch = text.match(/^(UAT \d+:\s*)/);
                          if (uatMatch && el.firstChild) {
                            const prefixLen = uatMatch[1].length;
                            const range = document.createRange();
                            range.setStart(el.firstChild, prefixLen);
                            range.setEnd(el.firstChild, text.length);
                            const sel = window.getSelection();
                            sel?.removeAllRanges();
                            sel?.addRange(range);
                          } else {
                            const range = document.createRange();
                            range.selectNodeContents(el);
                            const sel = window.getSelection();
                            sel?.removeAllRanges();
                            sel?.addRange(range);
                          }
                        });
                      }
                    }} data-placeholder="Section title" data-id="element-87" />
                          {testData[section.id]?.isRun && <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded-none" data-id="element-88">
                              Run
                            </span>}
                          <div className="relative flex-shrink-0" onMouseDown={e => e.stopPropagation()} data-id="element-89">
                            <button type="button" onClick={() => setOpenMenuId(openMenuId === section.id ? null : section.id)} className="w-6 h-6 flex items-center justify-center rounded-none hover:bg-stone-200 transition-colors duration-100 text-stone-400 hover:text-stone-600" aria-label="More options" data-id="element-90">
                              <MoreHorizontalIcon className="w-4 h-4" data-id="element-91" />
                            </button>
                            {openMenuId === section.id && <div className="absolute right-0 top-full mt-1 z-50 w-40 bg-white border border-stone-200 rounded-none shadow-lg py-1" data-id="element-92">
                                <button type="button" onClick={() => {
                          setOpenMenuId(null);
                          handleRequestDeletePage(section.id);
                        }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-100" data-id="element-93">
                                  <Trash2Icon className="w-3.5 h-3.5" data-id="element-94" />
                                  <span data-id="element-95">Delete</span>
                                </button>
                              </div>}
                          </div>
                          <button type="button" onClick={() => setCollapsedSections(prev => ({
                      ...prev,
                      [section.id]: !prev[section.id]
                    }))} className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-none hover:bg-stone-100 transition-colors duration-100 text-stone-400 hover:text-stone-600" aria-label={isCollapsed ? 'Expand section' : 'Collapse section'} data-id="element-96">
                            <ChevronRightIcon className={`w-4 h-4 transition-transform duration-150 ${isCollapsed ? '' : 'rotate-90'}`} data-id="element-97" />
                          </button>
                        </div>
                        {!isCollapsed && <>
                            {section.parentSection === 'product-hub' && <FeatureTemplate sectionId={section.id} personaOptions={personaOptionsWithIds} selectedPersonaIds={associations[section.id] || []} onPersonaSelectionChange={ids => handleAssociationChange(section.id, ids)} fields={featureFields[section.id] || {}} onFieldsChange={fields => setFeatureFields(prev => ({
                      ...prev,
                      [section.id]: fields
                    }))} onAddPersona={name => {
                      const scrollTop = mainRef.current?.scrollTop ?? 0;
                      const newPage = addPage('persona-hub');
                      updatePageTitleById(newPage.id, name);
                      setActiveSection('product-hub');
                      clearActivePage();
                      requestAnimationFrame(() => {
                        if (mainRef.current) {
                          mainRef.current.scrollTop = scrollTop;
                        }
                      });
                    }} data-id="element-98" />}
                            {section.parentSection === 'persona-hub' && <PersonaTemplate sectionId={section.id} featureOptions={featureOptionsWithIds} selectedFeatureIds={associations[section.id] || []} onFeatureSelectionChange={ids => handleAssociationChange(section.id, ids)} onAddFeature={(title, fields) => {
                      const scrollTop = mainRef.current?.scrollTop ?? 0;
                      const newPage = addPage('product-hub');
                      updatePageTitleById(newPage.id, title);
                      setFeatureFields(prev => ({
                        ...prev,
                        [newPage.id]: fields
                      }));
                      // Stay on persona-hub instead of navigating to the new feature
                      setActiveSection('persona-hub');
                      clearActivePage();
                      // Restore scroll position after re-render
                      requestAnimationFrame(() => {
                        if (mainRef.current) {
                          mainRef.current.scrollTop = scrollTop;
                        }
                      });
                    }} data-id="element-99" />}
                            {section.parentSection === 'tests' && <TestTemplate sectionId={section.id} featureOptions={featureOptionsWithIds} personaOptions={personaOptionsWithIds} associations={associations} testData={testData[section.id] || {
                      prototypeLink: '',
                      featureId: '',
                      personaIds: [],
                      testType: '',
                      componentFocuses: [],
                      hypothesis: '',
                      isRun: false
                    }} onTestDataChange={data => setTestData(prev => ({
                      ...prev,
                      [section.id]: data
                    }))} onRunTest={() => {
                      setTestData(prev => ({
                        ...prev,
                        [section.id]: {
                          ...(prev[section.id] || {
                            prototypeLink: '',
                            featureId: '',
                            personaIds: [],
                            testType: '',
                            componentFocuses: [],
                            hypothesis: '',
                            isRun: false
                          }),
                          isRun: true
                        }
                      }));
                      setCollapsedSections(prev => ({
                        ...prev,
                        [section.id]: true
                      }));
                    }} data-id="element-100" />}
                          </>}
                      </div>;
              };
              if (isTestHub) {
                return <>
                        {/* UAT Tests */}
                        <div className="mt-8" data-id="element-101">
                          <div className="flex items-center gap-3 mb-1" data-id="element-102">
                            <h2 className="text-sm font-semibold text-stone-400 uppercase tracking-wider whitespace-nowrap" data-id="element-103">
                              UAT Tests
                            </h2>
                            <div className="flex-1 h-px bg-stone-200" data-id="element-104" />
                          </div>
                          <p className="text-xs text-stone-400 mb-0" data-id="element-105">
                            Structured UAT test templates
                          </p>
                        </div>
                        {uatSections.length === 0 && <p className="text-sm text-stone-400 italic mt-4 mb-4" data-id="element-106">
                            No UAT tests created yet.
                          </p>}
                        {uatSections.map((section, i) => renderSectionCard(section, i === uatSections.length - 1))}

                        {/* Other Tests */}
                        <div className="mt-12" data-id="element-107">
                          <div className="flex items-center gap-3 mb-1" data-id="element-108">
                            <h2 className="text-sm font-semibold text-stone-400 uppercase tracking-wider whitespace-nowrap" data-id="element-109">
                              Other Tests
                            </h2>
                            <div className="flex-1 h-px bg-stone-200" data-id="element-110" />
                          </div>
                          <p className="text-xs text-stone-400 mb-0" data-id="element-111">
                            Freeform and custom tests
                          </p>
                        </div>
                        {otherSections.length === 0 && <p className="text-sm text-stone-400 italic mt-4 mb-4" data-id="element-112">
                            No other tests created yet.
                          </p>}
                        {otherSections.map((section, i) => renderSectionCard(section, i === otherSections.length - 1))}
                      </>;
              }
              return hubSections.map((section, i) => renderSectionCard(section, i === hubSections.length - 1));
            })()}
              </>}
          </div>
        </div>
      </main>

      {/* Onboarding Tour */}
      <OnboardingTour step={onboardingStep} onNext={handleOnboardingNext} onBack={handleOnboardingBack} onSkip={handleOnboardingSkip} data-id="element-113" />

      {pendingDeletePage && <DeleteConfirmDialog itemNoun={pendingDeleteNoun} onCancel={handleCancelDelete} onArchive={handleArchive} onDelete={handleConfirmDelete} data-id="element-114" />}

      {/* Tab Modal */}
      {showTabModal && <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-id="element-115">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => {
        setShowTabModal(false);
        setTabModalSearch('');
      }} data-id="element-116" />
          <div className="relative bg-white rounded-none shadow-2xl border border-stone-200 w-full max-w-2xl mx-4 max-h-[600px] overflow-hidden z-10" data-id="element-117">
            {/* Search */}
            <div className="p-4 border-b border-stone-200" data-id="element-118">
              <div className="relative" data-id="element-119">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-120" />
                <input type="text" value={tabModalSearch} onChange={e => setTabModalSearch(e.target.value)} placeholder="Open in new tab..." className="w-full pl-10 pr-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-none outline-none focus:border-stone-300 focus:bg-white transition-colors placeholder:text-stone-400" autoFocus data-id="element-121" />
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[480px] p-4" data-id="element-122">
              {/* Today */}
              {filterPages(todayPages).length > 0 && <div className="mb-6" data-id="element-123">
                  <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 px-2" data-id="element-124">
                    Today
                  </h3>
                  <div className="space-y-1" data-id="element-125">
                    {filterPages(todayPages).map(page => <button key={page.id} onClick={() => handleOpenInNewTab(page.parentSection, page.id, page.title)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-none hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-126">
                        <FileTextIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-127" />
                        <div className="flex-1 min-w-0" data-id="element-128">
                          <div className="text-sm font-medium text-stone-900 truncate" data-id="element-129">
                            {page.title}
                          </div>
                          <div className="text-xs text-stone-400" data-id="element-130">
                            {SECTION_TITLES[page.parentSection]}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>}

              {/* Yesterday */}
              {filterPages(yesterdayPages).length > 0 && <div className="mb-6" data-id="element-131">
                  <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 px-2" data-id="element-132">
                    Yesterday
                  </h3>
                  <div className="space-y-1" data-id="element-133">
                    {filterPages(yesterdayPages).map(page => <button key={page.id} onClick={() => handleOpenInNewTab(page.parentSection, page.id, page.title)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-none hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-134">
                        <FileTextIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-135" />
                        <div className="flex-1 min-w-0" data-id="element-136">
                          <div className="text-sm font-medium text-stone-900 truncate" data-id="element-137">
                            {page.title}
                          </div>
                          <div className="text-xs text-stone-400" data-id="element-138">
                            {SECTION_TITLES[page.parentSection]}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>}

              {/* Past Week */}
              {filterPages(pastWeekPages).length > 0 && <div className="mb-6" data-id="element-139">
                  <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 px-2" data-id="element-140">
                    Past week
                  </h3>
                  <div className="space-y-1" data-id="element-141">
                    {filterPages(pastWeekPages).map(page => <button key={page.id} onClick={() => handleOpenInNewTab(page.parentSection, page.id, page.title)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-none hover:bg-stone-50 transition-colors duration-100 text-left" data-id="element-142">
                        <FileTextIcon className="w-4 h-4 flex-shrink-0 text-stone-400" data-id="element-143" />
                        <div className="flex-1 min-w-0" data-id="element-144">
                          <div className="text-sm font-medium text-stone-900 truncate" data-id="element-145">
                            {page.title}
                          </div>
                          <div className="text-xs text-stone-400" data-id="element-146">
                            {SECTION_TITLES[page.parentSection]}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </div>}

              {/* Empty state */}
              {filterPages(todayPages).length === 0 && filterPages(yesterdayPages).length === 0 && filterPages(pastWeekPages).length === 0 && <div className="py-12 text-center" data-id="element-147">
                    <FileTextIcon className="w-12 h-12 mx-auto text-stone-300 mb-3" data-id="element-148" />
                    <p className="text-sm text-stone-400" data-id="element-149">
                      {tabModalSearch.trim() ? 'No pages found' : 'No recent pages'}
                    </p>
                  </div>}
            </div>
          </div>
        </div>}
    </div>;
}